<template>
  <div class="support-page">
    <!-- Page Header -->
    <div class="page-header">
      <div class="header-inner">
        <div>
          <h1 class="header-title">Suporte ao Utilizador</h1>
          <p class="header-sub">
            Encontre soluções ou envie um pedido à nossa equipa
          </p>
        </div>
      </div>
    </div>

    <div class="page-body">
      <div class="row q-col-gutter-lg">
        <!-- FAQ Column -->
        <div class="col-12 col-lg-8">
          <div class="section-intro">
            <div class="intro-icon-wrap">
              <q-icon name="help_outline" size="20px" />
            </div>
            <div>
              <h2 class="intro-title">Perguntas Frequentes</h2>
              <p class="intro-sub">
                Encontre respostas para as dúvidas mais comuns sobre a
                GoldenKey.
              </p>
            </div>
          </div>

          <div class="faq-list">
            <div
              v-for="(faq, index) in faqs"
              :key="index"
              class="faq-card"
              :class="{ 'faq-card--open': faq.open }"
            >
              <button
                type="button"
                class="faq-toggle"
                @click="toggleAccordion(index)"
              >
                <span class="faq-question">{{ faq.question }}</span>
                <q-icon
                  :name="faq.open ? 'expand_less' : 'expand_more'"
                  size="22px"
                  class="faq-chevron"
                />
              </button>

              <q-slide-transition>
                <div v-show="faq.open">
                  <div class="faq-divider"></div>
                  <div class="faq-answer" v-html="faq.answer"></div>
                </div>
              </q-slide-transition>
            </div>
          </div>
        </div>

        <!-- Support Form Column -->
        <div class="col-12 col-lg-4">
          <div class="support-card">
            <div class="support-card-header">
              <div class="support-icon">
                <q-icon name="headset_mic" size="26px" />
              </div>
              <div>
                <h3 class="support-title">Ainda precisa de ajuda?</h3>
                <p class="support-subtitle">
                  Se não encontrou a resposta, envie um pedido à nossa equipa de
                  suporte.
                </p>
              </div>
            </div>

            <div class="card-divider"></div>

            <q-form @submit="submitForm" class="support-form">
              <div class="field-wrap">
                <label class="field-label">
                  Tipo de problema <span class="field-req">*</span>
                </label>
                <!-- <q-select
                  v-model="submission.escolha"
                  :options="opcoes"
                  placeholder="Selecione uma opção"
                  outlined
                  class="form-input"
                  lazy-rules
                  :rules="[(val) => !!val || 'Selecione uma opção']"
                >
                  <template v-slot:prepend>
                    <q-icon name="title" size="18px" />
                  </template>
                </q-select> -->

                <q-select
                  v-model="submission.escolha"
                  :options="opcoes"
                  placeholder="Selecione uma opção"
                  outlined
                  class="form-input"
                  lazy-rules
                  emit-value
                  map-options
                >
                  <template v-slot:prepend>
                    <q-icon name="title" size="18px" />
                  </template>
                </q-select>
              </div>

              <div class="field-wrap">
                <label class="field-label">
                  Descrição do problema <span class="field-req">*</span>
                </label>
                <q-input
                  v-model="submission.description"
                  placeholder="Ex: Ao tentar adicionar novas imagens a aplicação rejeita as imagens"
                  outlined
                  type="textarea"
                  rows="5"
                  class="form-input"
                  lazy-rules
                  :rules="[(val) => !!val || 'Descreva o problema']"
                >
                  <template v-slot:prepend>
                    <q-icon name="notes" size="18px" />
                  </template>
                </q-input>
              </div>

              <div class="field-wrap">
                <label class="field-label">
                  Contacto
                  <span class="field-optional">opcional</span>
                </label>
                <q-input
                  v-model="submission.telefone"
                  outlined
                  placeholder="+258 84 123 4567"
                  mask="+258 ## ### ####"
                  unmasked-value
                  class="form-input"
                  :rules="[
                    (val) => !val || val.length >= 9 || 'Contacto inválido',
                  ]"
                  lazy-rules
                >
                  <template v-slot:prepend>
                    <q-icon name="phone" size="18px" />
                  </template>
                </q-input>
              </div>

              <div class="form-actions">
                <button type="button" class="btn-cancel" @click="resetForm">
                  Limpar
                </button>
                <button type="submit" class="btn-save" :disabled="isSubmitting">
                  <q-spinner v-if="isSubmitting" size="16px" class="q-mr-sm" />
                  <span>Enviar pedido</span>
                  <q-icon
                    v-if="!isSubmitting"
                    name="send"
                    size="16px"
                    class="q-ml-sm"
                  />
                </button>
              </div>
            </q-form>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from "vue";
import store from "@/vuex/store";
import Swal from "sweetalert2";
import { useRouter } from "vue-router";

const user = ref({ name: "", email: "" });
const phone = ref("");
const router = useRouter();
const isSubmitting = ref(false);

const opcoes = [
  {
    label: "Selecione uma opção",
    value: "",
    disable: true,
  },
  {
    label: "Problemas ao publicar um imóvel",
    value: "Problemas ao publicar um imóvel",
  },
  {
    label: "Não consigo editar ou eliminar meu anúncio",
    value: "Não consigo editar ou eliminar meu anúncio",
  },
  {
    label: "Imagens não carregam corretamente",
    value: "Imagens não carregam corretamente",
  },
  {
    label: "Erro ao pesquisar ou filtrar imóveis",
    value: "Erro ao pesquisar ou filtrar imóveis",
  },
  {
    label: "Problema com dados do perfil/conta",
    value: "Problema com dados do perfil/conta",
  },
  {
    label: "Não consigo ver meu imóvel publicado",
    value: "Não consigo ver meu imóvel publicado",
  },
  {
    label: "Erro ao adicionar fotos do imóvel",
    value: "Erro ao adicionar fotos do imóvel",
  },
  {
    label: "Problema ao atualizar dados pessoais",
    value: "Problema ao atualizar dados pessoais",
  },
  {
    label: "Aplicação trava ou fecha sozinha",
    value: "Aplicação trava ou fecha sozinha",
  },
  {
    label: "Erro ao carregar página de detalhes do imóvel",
    value: "Erro ao carregar página de detalhes do imóvel",
  },
  {
    label: "Erro ao gerar relatório de propriedades",
    value: "Erro ao gerar relatório de propriedades",
  },
  {
    label: "Outro",
    value: "Outro",
  },
];

onMounted(async () => {
  user.value = store.state.auth.user ?? { name: "", email: "" };
  const users = await store.dispatch("admin/getUsers");
  const currentUser = users.find((u) => u.email === user.value.email);
  phone.value = currentUser?.phone || "";
});

const faqs = ref([
  {
    question: "Como posso publicar um imóvel na GoldenKey?",
    answer:
      '<strong>Para publicar um imóvel no GoldenKey</strong>, comece por iniciar sessão na sua conta. Em seguida, aceda ao painel do utilizador e clique na opção <strong>"Publicar Imóvel"</strong>. Preencha todas as informações solicitadas, incluindo o título, descrição, localização, preço, características e fotografias do imóvel. Antes de submeter o anúncio, verifique se todos os dados estão corretos. Após o envio, a equipa do GoldenKey analisará o anúncio e, caso cumpra os requisitos da plataforma, será publicado e ficará disponível para todos os utilizadores interessados.',
    open: false,
  },
  {
    question: "Quais são os requisitos para publicar um imóvel na GoldenKey?",
    answer:
      "<strong>Para publicar um imóvel na GoldenKey</strong>, é necessário possuir uma conta registada e iniciar sessão na plataforma. O imóvel deve conter informações completas e verdadeiras, incluindo o título, descrição, localização, preço, tipo de imóvel e fotografias de boa qualidade. Certifique-se também de que o anúncio cumpre as políticas da GoldenKey e de que possui autorização para anunciar o imóvel. Após a submissão, o anúncio será analisado pela equipa de moderação antes de ser publicado na plataforma.",
    open: false,
  },
  {
    question: "Como Editar ou remover um imóvel publicado na GoldenKey?",
    answer:
      '<strong>Para editar ou remover um imóvel publicado na GoldenKey</strong>, inicie sessão na sua conta e aceda ao painel do utilizador. Navegue até à secção de "Meus Anúncios" e selecione o imóvel que deseja modificar ou eliminar. Siga as instruções para concluir a operação.',
    open: false,
  },
  {
    question: "Como Efetuar pagamentos no GoldenKey?",
    answer:
      '<strong>Para efetuar pagamentos no GoldenKey</strong>, inicie sessão na sua conta e aceda ao painel do utilizador. Navegue até à secção de "Pagamentos" ou "Subscrições" e selecione o serviço ou plano que deseja pagar. Escolha o método de pagamento disponível e introduza os dados necessários. Confirme a transação e aguarde a validação do pagamento. Após a confirmação, receberá uma notificação de sucesso no sistema ou por e-mail.',
    open: false,
  },
  {
    question: "Problemas com minha conta?",
    answer:
      "<strong>Problemas com minha conta?</strong> Caso esteja a enfrentar problemas com a sua conta, comece por verificar se os dados de acesso estão corretos, incluindo o e-mail e a palavra-passe. Se não conseguir iniciar sessão, utilize a opção de recuperação de password disponível na página de login. Certifique-se também de que a sua conta não foi bloqueada ou suspensa por violação das políticas da plataforma. Se o problema persistir, entre em contacto com a equipa de suporte da GoldenKey para assistência adicional.",
    open: false,
  },
  {
    question: "O imóvel não aparece ou foi rejeitado?",
    answer:
      "<strong>O imóvel não aparece ou foi rejeitado?</strong> Se o seu imóvel não aparece na plataforma ou foi rejeitado, verifique primeiro se o anúncio cumpre todos os requisitos da GoldenKey, incluindo informações completas, imagens de qualidade e descrição correta. Confirme também se o anúncio já foi aprovado pela equipa de moderação, pois pode ainda estar em análise. Caso tenha sido rejeitado, verifique a notificação recebida para entender o motivo e faça as devidas correções. Após ajustar os dados, poderá reenviar o anúncio para nova análise. Se o problema persistir, entre em contacto com o suporte da GoldenKey para mais informações.",
    open: false,
  },
  {
    question: "Como posso alterar meus dados pessoais?",
    answer:
      '<strong>Como posso alterar meus dados pessoais?</strong> Para alterar os seus dados pessoais, inicie sessão na sua conta e aceda ao painel do utilizador. Em seguida, entre na secção de "Perfil" ou "Configurações da conta". A partir daí, poderá atualizar informações como nome, e-mail, número de telefone e outros dados pessoais. Após realizar as alterações, não se esqueça de guardar as informações atualizadas. Se não conseguir alterar algum dado específico, entre em contacto com a equipa de suporte da GoldenKey.',
    open: false,
  },
  {
    question: "Como Alterar preço do imóvel?",
    answer:
      '<strong>Como alterar o preço do imóvel?</strong> Para alterar o preço de um imóvel, inicie sessão na sua conta e aceda ao painel do utilizador. Em seguida, vá até à secção de "Meus Anúncios" e selecione o imóvel que pretende editar. Clique na opção de editar anúncio e atualize o campo referente ao preço. Após fazer a alteração, não se esqueça de guardar as mudanças para que o novo valor seja atualizado na plataforma. Se o anúncio já estiver aprovado, a alteração poderá ser novamente revista pela equipa de moderação.',
    open: false,
  },
  {
    question: "Porque não consigo visualizar o imóvel publicado?",
    answer:
      '<strong>Porque não consigo visualizar o imóvel publicado?</strong> Se não consegue visualizar o seu imóvel publicado, verifique primeiro se o anúncio já foi aprovado pela equipa de moderação da GoldenKey, pois anúncios em análise podem ainda não estar visíveis. Confirme também se o imóvel não foi rejeitado por não cumprir os requisitos da plataforma ou por informações incompletas. Outro motivo pode ser a configuração de visibilidade do anúncio, que pode estar como "inativo" ou "privado". Se tudo estiver correto e o problema persistir, tente atualizar a página ou limpar a cache do navegador. Caso continue sem aparecer, entre em contacto com a equipa de suporte da GoldenKey para verificação.',
    open: false,
  },
]);

function categorizarFrase(frase) {
  const fraseNormalizada = frase.toLowerCase();

  const wordlistBilling = [
    "fatura",
    "cobrança",
    "pagamento",
    "pagar",
    "boleto",
    "invoice",
    "billing",
    "reembolso",
    "cartão de crédito",
    "valor cobrado",
    "assinatura",
    "mensalidade",
    "recibo",
    "cobrar",
  ];

  const wordlistTechnical = [
    "problemas ao publicar um imóvel",
    "não consigo editar ou eliminar meu anúncio",
    "imagens não carregam corretamente",
    "erro ao pesquisar ou filtrar imóveis",
    "problema com dados do perfil/conta",
    "não consigo ver meu imóvel publicado",
    "problema de performance/aplicação lenta",
    "erro ao adicionar fotos do imóvel",
    "problema ao atualizar dados pessoais",
    "erro ao carregar página de detalhes do imóvel",
    "erro ao gerar relatório de propriedades",
    "outro",
  ];

  const wordlistFeatureRequest = [
    "sugestão",
    "seria bom",
    "gostaria que",
    "poderia adicionar",
    "nova funcionalidade",
    "feature",
    "update",
    "melhoria",
    "implementar",
    "seria interessante",
    "poderiam incluir",
    "falta a opção",
  ];

  const categorias = [
    { nome: "billing", lista: wordlistBilling, prioridade: "high" },
    { nome: "technical", lista: wordlistTechnical, prioridade: "medium" },
    {
      nome: "feature_request",
      lista: wordlistFeatureRequest,
      prioridade: "medium",
    },
  ];

  for (const categoria of categorias) {
    const encontrado = categoria.lista.some((termo) =>
      fraseNormalizada.includes(termo),
    );
    if (encontrado) {
      return { categoria: categoria.nome, prioridade: categoria.prioridade };
    }
  }

  return { categoria: "general", prioridade: "low" };
}

const submission = ref({
  description: "",
  escolha: "",
  telefone: "",
});

const toggleAccordion = (index) => {
  faqs.value[index].open = !faqs.value[index].open;
};

const resetForm = () => {
  submission.value = {
    description: "",
    escolha: "",
    telefone: "",
  };
};

const submitForm = async () => {
  // if (!submission.value.escolha) {
  //   Swal.fire({
  //     title: "Atenção",
  //     text: "Selecione uma opção para continuar.",
  //     icon: "warning",
  //     confirmButtonText: "OK",
  //   });
  //   return;
  // }

  isSubmitting.value = true;

  if (!submission.value.escolha) {
    submission.value.escolha = "Outro";
  }

  let phoneText = submission.value.telefone || phone.value || "";
  const resultado = categorizarFrase(submission.value.escolha);
  const descriptionText = submission.value.description.trim();
  const formData = {
    title: `${submission.value.escolha} - GoldenKey`,
    description: phoneText
      ? `${descriptionText} \n${phoneText}`
      : descriptionText,
    category: resultado.categoria,
    priority: resultado.prioridade,
  };

  try {
    await store.dispatch("ticket/storeTicket", formData);

    Swal.fire({
      title: "Sucesso!",
      text: "Pedido enviado com sucesso. \n O seu pedido está sendo acompanhado por nossa equipe.\n Brevemente Entraremos em Contacto.",
      icon: "success",
      confirmButtonText: "OK",
    }).then(() => {
      router.push("/support");
    });

    resetForm();
  } catch (err) {
    console.error(err);
    Swal.fire({
      title: "Erro!",
      text: "Houve um problema ao enviar o pedido. Tente novamente.",
      icon: "error",
      confirmButtonText: "OK",
    });
  } finally {
    isSubmitting.value = false;
  }
};
</script>
<style scoped>
.support-page {
  min-height: 100vh;
  background: #f4f6fb;
}

/* ── Header ── */
.page-header {
  background: #0d1b2a;
  padding: 32px 24px 28px;
  border-bottom: 1px solid rgba(212, 175, 55, 0.15);
}
.header-inner {
  max-width: 1200px;
  margin: 0 auto;
}
.header-title {
  font-size: 22px;
  font-weight: 700;
  color: #ffffff;
  margin: 0 0 4px;
}
.header-sub {
  font-size: 13px;
  color: rgba(212, 175, 55, 0.7);
  margin: 0;
  letter-spacing: 0.3px;
}

/* ── Body ── */
.page-body {
  max-width: 1200px;
  margin: 0 auto;
  padding: 28px 16px 48px;
}

/* ── Section intro (FAQ heading) ── */
.section-intro {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-bottom: 18px;
}
.intro-icon-wrap {
  width: 42px;
  height: 42px;
  border-radius: 12px;
  background: rgba(212, 175, 55, 0.12);
  color: #d4af37;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.intro-title {
  font-size: 18px;
  font-weight: 700;
  color: #0d1b2a;
  margin: 0 0 3px;
}
.intro-sub {
  font-size: 13px;
  color: #6b7280;
  margin: 0;
}

/* ── FAQ list ── */
.faq-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.faq-card {
  background: #ffffff;
  border: 1px solid #e8ecf4;
  border-radius: 14px;
  overflow: hidden;
  transition: border-color 0.15s, box-shadow 0.15s;
}
.faq-card:hover {
  border-color: rgba(212, 175, 55, 0.35);
}
.faq-card--open {
  border-color: rgba(212, 175, 55, 0.4);
  box-shadow: 0 2px 16px rgba(13, 27, 42, 0.06);
}

.faq-toggle {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: 14px;
  padding: 16px 20px;
  background: transparent;
  border: none;
  cursor: pointer;
  text-align: left;
  transition: background 0.15s;
}
.faq-toggle:hover {
  background: #f7f8fc;
}
.faq-question {
  font-size: 14px;
  font-weight: 600;
  color: #0d1b2a;
  line-height: 1.4;
}
.faq-chevron {
  color: #d4af37;
  flex-shrink: 0;
}

.faq-divider {
  height: 1px;
  background: #f0f2f7;
  margin: 0 20px;
}
.faq-answer {
  padding: 14px 20px 18px;
  font-size: 13.5px;
  color: #374151;
  line-height: 1.6;
}
.faq-answer :deep(strong) {
  color: #0d1b2a;
  font-weight: 700;
}

/* ── Support card ── */
.support-card {
  background: #ffffff;
  border: 1px solid #e8ecf4;
  border-radius: 18px;
  box-shadow: 0 2px 16px rgba(13, 27, 42, 0.07);
  overflow: hidden;
  position: sticky;
  top: 20px;
}

.support-card-header {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  padding: 22px 22px 20px;
}
.support-icon {
  width: 46px;
  height: 46px;
  border-radius: 12px;
  background: rgba(212, 175, 55, 0.12);
  color: #d4af37;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.support-title {
  margin: 0 0 4px;
  font-size: 15px;
  font-weight: 700;
  color: #0d1b2a;
}
.support-subtitle {
  margin: 0;
  font-size: 12.5px;
  color: #6b7280;
  line-height: 1.5;
}

.card-divider {
  height: 1px;
  background: #f0f2f7;
}

/* ── Form ── */
.support-form {
  padding: 22px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.field-wrap {
  margin-bottom: 4px;
}
.field-label {
  display: block;
  font-size: 12.5px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 6px;
  letter-spacing: 0.2px;
}
.field-req {
  color: #dc2626;
  margin-left: 2px;
}
.field-optional {
  display: inline-block;
  margin-left: 6px;
  font-size: 11px;
  font-weight: 500;
  color: #9ca3af;
  background: #f3f4f6;
  border-radius: 4px;
  padding: 1px 6px;
  vertical-align: middle;
}

:deep(.form-input .q-field__control) {
  border-radius: 11px !important;
  background: #fafbfc !important;
}
:deep(.form-input.q-field--focused .q-field__control) {
  border-color: #d4af37 !important;
  box-shadow: 0 0 0 3px rgba(212, 175, 55, 0.12);
}
:deep(.form-input .q-icon) {
  color: #9ca3af;
}
:deep(.form-input.q-field--focused .q-icon) {
  color: #d4af37;
}

/* ── Buttons ── */
.form-actions {
  display: flex;
  gap: 12px;
  margin-top: 16px;
}
.btn-cancel {
  flex: 1;
  padding: 12px 20px;
  border-radius: 11px;
  border: 1px solid #d1d5db;
  background: transparent;
  color: #6b7280;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s;
}
.btn-cancel:hover {
  background: #f3f4f6;
}
.btn-save {
  flex: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px 20px;
  border-radius: 11px;
  border: none;
  background: #0d1b2a;
  color: #d4af37;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  letter-spacing: 0.3px;
  transition: background 0.15s, opacity 0.15s;
}
.btn-save:hover {
  background: #162940;
}
.btn-save:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

@media (max-width: 1023px) {
  .support-card {
    position: static;
    margin-top: 8px;
  }
}

@media (max-width: 600px) {
  .page-header {
    padding: 24px 16px 22px;
  }
  .header-title {
    font-size: 19px;
  }
  .form-actions {
    flex-direction: column-reverse;
  }
  .btn-cancel,
  .btn-save {
    width: 100%;
  }
}
</style>
